import { Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppAuth } from "./auth";
import { Loading } from "./components/ui";
import { Shell } from "./components/Shell";
import { SignIn } from "./pages/SignIn";
import { CreateAccount } from "./pages/CreateAccount";
import { ResetPassword } from "./pages/ResetPassword";
import { CheckEmail } from "./pages/CheckEmail";
import { Dashboard } from "./pages/Dashboard";
import { Book } from "./pages/Book";
import { Visits } from "./pages/Visits";
import { VisitRoom } from "./pages/VisitRoom";
import { DoctorProfile } from "./pages/DoctorProfile";
import { Messages } from "./pages/Messages";
import { Concierge } from "./pages/Concierge";
import { Care } from "./pages/Care";
import { Account } from "./pages/Account";
import { Settings } from "./pages/Settings";
import { Help } from "./pages/Help";

function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAppAuth();
  if (!isLoaded) return <Loading label="Checking your session…" />;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/create-account" element={<CreateAccount />} />
      {/* Marketing hands off to /signup (with name/email/tier query params); alias it to the
          create-account screen so the prefill + chosen tier aren't lost to the catch-all. */}
      <Route path="/signup" element={<CreateAccount />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/check-email" element={<CheckEmail />} />
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="visits" element={<Visits />} />
        <Route path="visits/book" element={<Book />} />
        <Route path="visits/:id/room" element={<VisitRoom />} />
        <Route path="doctors/:id" element={<DoctorProfile />} />
        <Route path="messages" element={<Messages />} />
        <Route path="concierge" element={<Concierge />} />
        <Route path="care" element={<Care />} />
        <Route path="account" element={<Account />} />
        <Route path="summary" element={<Navigate to="/care" replace />} />
        <Route path="settings" element={<Settings />} />
        <Route path="help" element={<Help />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
